import BoilerplateForm from './boilerplate_form';
import { html, nothing } from 'lit';
import { ref } from 'lit/directives/ref.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

export class WishlistMoveToBoardForm extends BoilerplateForm {
  static get properties() {
    return {
      boards: { type: Array, attribute: true },
      currentBoard: { type: Object, attribute: true },
      items: { type: Array, attribute: true },
    };
  }

  constructor() {
    super();
    this.useUrlParams = false;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);

    if (changedProperties.has('boards')) {
      const currentId = this.currentBoard?.id;
      this.currentBoard = this.boards.find((board) => board.id === currentId);
      this.requestUpdate();
    }
  }

  async handleSubmit(event) {
    event.preventDefault();

    this.loading = true;
    this.busy = true;
    this.errors = {};
    this.genericErrorSegments = [];

    const fields = this.objectData;

    const boardIds = Object.values(fields).map((item) => parseInt(item));

    try {
      await window.wishlist.moveProductToBoard(
        this.items,
        this.currentBoard.id,
        boardIds
      );

      const firstBoardId = boardIds[0];

      if (firstBoardId) {
        window.sessionStorage.setItem('wishlist_adding_board', firstBoardId);
      }

      this.handleSuccess();
      window.dispatchEvent(
        new CustomEvent('analytics:gtag', {
          detail: {
            event: 'wishlist_move_to_board',
            params: {},
          },
        })
      );
    } catch (error) {
      const errorArr = Array.isArray(error) ? error : [error];
      this.handleErrors(errorArr);
    } finally {
      this.loading = false;
      this.busy = false;

      window.scrollTo({ top: 0 });
    }
  }

  renderEmoji(emoji) {
    const emojiObj =
      window.wishlist_emojis.find((e) => e.value === emoji) ||
      window.wishlist_emojis.find((e) => e.default);

    if (!emojiObj) return nothing;

    if (emojiObj.type === 'svg') {
      return html`${unsafeHTML(emojiObj.display)}`;
    }

    if (emojiObj.type === 'image') {
      return html`<img
        src="${emojiObj.display}"
        width="12"
        height="12"
        alt="${emojiObj.value}"
        loading="lazy"
      />`;
    }

    if (emojiObj.type === 'emoji') {
      return html`<span>${unsafeHTML(emojiObj.display)}</span>`;
    }
  }

  renderFormElements() {
    if (!this.boards) return nothing;

    if (this.boards.length <= 1) {
      return html`${unsafeHTML(window.theme.strings.wishlist.move_one_board)}`;
    }

    return html`<fieldset class="board_selector">
      ${repeat(
      window.wishlist.boards,
      (board) => board.id,
      (board) => {
        const colour = window.wishlist_colours.find((c) => c.value === board?.colour)?.colour || this.board?.colour || window.wishlist_colours.find((c) => c.default === true)?.colour || 'white';

        if (this.currentBoard?.id === board.id && !this.detail?.showAddingBoard) {
          return nothing;
        }

        return html`
            <label for="board-${board.id}" class="board_select_label" style="--wishlist-colour: ${colour}">
              <input type="checkbox" id="board-${board.id}" name="board-${board.id}" value=${board.id} class="board_select_input" />
              <span class="board_select_label_text">${this.renderEmoji(board.emoji)} ${board.name}</span>
            </label>
          `;
      },
    )}
    </fieldset>`;
  }

  handleSuccess() {
    this.errors = {};
    this.genericErrorSegments = [];
    this.loading_save = false;
    this.busy = false;

    const movedEvent = new CustomEvent('wishlist:move', {
      detail: {
        movedItems: this.items,
      },
    });

    document.dispatchEvent(movedEvent);

    const event = new CustomEvent('modal-wishlist-move-to-board:close');
    document.dispatchEvent(event);
  }

  renderInitial() {
    return html`<form
      ${ref((el) => (this.form = el))}
      method=${ifDefined(this.method)}
      ?novalidate=${this.novalidate}
      enctype=${ifDefined(this.enctype)}
      class="${this.id}"
      action="javascript:void(0)"
    >
      ${this.renderFormElements()} ${this.renderButton()} ${this.renderErrors()}
    </form>`;
  }

  openCreateModal() {
    const event = new CustomEvent('modal-wishlist-create-board:open', {
      detail: {
        currentBoard: this.currentBoard,
        items: this.items,
      },
    });
    document.dispatchEvent(event);

    const closeEvent = new CustomEvent('modal-wishlist-move-to-board:close');
    document.dispatchEvent(closeEvent);
  }

  renderButton() {
    if (!this.boards || this.boards.length === 0) {
      return nothing;
    }

    return html`
      <div class="form_same_line justify_center">
        <button
          type="button"
          @click=${this.openCreateModal}
          class="btn btn-tertiary w-100"
          aria-haspopup="dialog"
          aria-label="open create new form"
          ?disabled=${this.busy || this.items?.length === 0}
          aria-busy=${this.busy
        ? 'true'
        : 'false'}
        >
          ${unsafeHTML(window.theme.strings.wishlist.create_board)}
        </button>
        <button
          type="submit"
          class="btn btn-primary w-100"
          aria-label="Save"
          ?disabled=${this.loading || this.busy || this.items?.length === 0}
          aria-busy=${this.loading || this.busy
        ? 'true'
        : 'false'}
        >
          ${this.loading
        ? html`<bouncing-ellipsis></bouncing-ellipsis>`
        : html`${unsafeHTML(window.theme.strings.wishlist.save)}`}
        </button>
      </div>
    `;
  }
}

window.customElements.get('wishlist-move-to-board-form') ||
  window.customElements.define(
    'wishlist-move-to-board-form',
    WishlistMoveToBoardForm
  );
